/**
 * @documental
 * @export Grid
 * @import
 * import { Grid } from "capitalrx-components-2.0/lib/layout";
 */

import { FC, PropsWithChildren } from "react";
import { getCells } from "./helpers";
import { BaseProps } from "../../BaseProps";
import { withSubComponents } from "../../utilities/withSubComponents";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import classNames from "classnames";
import { Break } from "./Break";
import { Span } from "./Span";
import { Spread } from "./Spread";
import styles from "./styles.module.scss";
import { Spacing } from "../../layout";

/**
 * The `Grid` component properties.
 */
export interface GridProps extends BaseProps {
  /**
   * The vertical spacing between grid rows. If not specified defaults to "medium".
   */
  readonly spacing?: Spacing;
}

/**
 * The `Grid` component subcomponents.
 */
export interface GridSubComponents {
  /**
   * A component which specifies how many columns its child must span within the grid.
   */
  readonly Span: typeof Span;

  /**
   * A component which spreads each of its children and out the specified number of columns across the `Grid`.
   */
  readonly Spread: typeof Spread;

  /**
   * A component which indicates a row break within the grid.
   */
  readonly Break: typeof Break;
}

/**
 * Lays out content against a 12-column grid. Children placed within the grid are automatically placed
 * within columns and spread across the full width of the grid. Additional `Span` and `Break` components
 * can be placed to define breakpoints for rows and control how many columns a particular component
 * spans across.
 */
export const Grid = withSubComponents<FC<PropsWithChildren<GridProps>>, GridSubComponents>(
  {
    Span,
    Spread,
    Break,
  },
  (props) => {
    const { spacing = "medium", children } = props;

    const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
      className: [styles.Grid, "grid", `space-${spacing}`],
      testIdPrefix: "grid",
    });

    const cells = getCells(children, 12);

    return (
      <div {...baseHtmlProps}>
        {cells.map((cell) => (
          <div key={cell.key} className={classNames(styles.Cell, "cell")} style={{ gridColumnEnd: `span ${cell.span}` }}>
            {cell.child}
          </div>
        ))}
      </div>
    );
  },
);
